import { useEffect, useRef, useState } from "react";
import FilePicker from "@/components/FilePicker";
import { NavButtons } from "../components/NavButtons";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getCached, setCached } from "@/lib/cache";
import { toast } from "sonner";
import AccordionCard, { ExpandAllToggle } from "@/components/AccordionCard";
import { BulkBar, SelectBox, useBulkSelection } from "@/components/BulkSelect";
import FilterPills from "@/components/FilterPills";
import TaskNotes from "@/components/TaskNotes";
import {
  ArrowLeft, Plus, Trash, CheckCircle, Circle, PaperPlaneTilt,
  UploadSimple, DownloadSimple, PencilSimple, Calendar, Tag,
  CaretDown, CaretUp, Archive, ArrowCounterClockwise,
} from "@phosphor-icons/react";

const STATUSES = ["todo", "in_progress", "complete"];
const PRIORITIES = ["low", "normal", "high"];
const RECURRENCES = [
  { k: "none", label: "One-off (no recurrence)" },
  { k: "daily", label: "Daily" },
  { k: "weekly", label: "Weekly" },
  { k: "biweekly", label: "Every 2 weeks" },
  { k: "monthly", label: "Monthly" },
  { k: "quarterly", label: "Quarterly" },
  { k: "yearly", label: "Yearly" },
];
const RECURRENCE_LABEL = Object.fromEntries(RECURRENCES.map(r => [r.k, r.label]));

const parseDay = (s) => {
  if (!s) return null;
  const iso = String(s).slice(0, 10);
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const fmtDate = (d) => {
  if (!d) return "—";
  const p = parseDay(d) || new Date(d);
  if (isNaN(p.getTime())) return d;
  return p.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const fmtTime = (t) => {
  if (!t) return "";
  const [hh, mm] = String(t).split(":").map(Number);
  if (Number.isNaN(hh)) return t;
  const period = hh >= 12 ? "PM" : "AM";
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${String(mm || 0).padStart(2, "0")} ${period}`;
};

const fmtDateTime = (d, t) => {
  if (!d) return "—";
  const dateStr = fmtDate(d);
  return t ? `${dateStr} · ${fmtTime(t)}` : dateStr;
};

function DueBadge({ due, dueTime, status }) {
  if (!due) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = parseDay(due);
  if (!d) return null;
  const days = Math.round((d - today) / 86400000);
  let bg = "#e5e7eb", color = "#374151", label = fmtDateTime(due, dueTime);
  if (status !== "complete") {
    if (days < 0) { bg = "#fee2e2"; color = "#991b1b"; label = `Overdue by ${-days}d${dueTime ? ` · ${fmtTime(dueTime)}` : ""}`; }
    else if (days === 0) { bg = "#fef3c7"; color = "#78350f"; label = `Due today${dueTime ? ` · ${fmtTime(dueTime)}` : ""}`; }
    else { bg = "#fef3c7"; color = "#78350f"; label = `Due in ${days}d${dueTime ? ` · ${fmtTime(dueTime)}` : ""}`; }
  }
  return <span className="label px-2 py-0.5" style={{ background: bg, color, border: `1px solid ${bg}` }}>{label}</span>;
}

function PriorityDot({ p }) {
  const color = p === "high" ? "#dc2626" : p === "low" ? "#9ca3af" : "#3b82f6";
  return <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} title={p} />;
}

function NewTaskForm({ clients, admins, onCreated }) {
  const [form, setForm] = useState({
    title: "", description: "", priority: "normal",
    due_date: "", due_time: "", assignee_user_id: "",
    client_user_id: "", recurrence: "none",
  });
  const [subs, setSubs] = useState([{ title: "", due_date: "", due_time: "", notes: "" }]);
  const [subsOpen, setSubsOpen] = useState(false);
  const filesRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [recentTplIds, setRecentTplIds] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem("recent-task-templates") || "[]");
      return Array.isArray(v) ? v.slice(0, 3) : [];
    } catch { return []; }
  });
  const [tplOpen, setTplOpen] = useState(false);
  const [tplPickTemplate, setTplPickTemplate] = useState("");
  const [tplPickStep, setTplPickStep] = useState("");
  const [saveTpl, setSaveTpl] = useState(false);
  const [saveTplTarget, setSaveTplTarget] = useState(""); // template_id or "__new__"
  const [saveTplNewName, setSaveTplNewName] = useState("");
  const [applyingTpl, setApplyingTpl] = useState(false);
  useEffect(() => {
    api.get("/admin/templates").then(r => setTemplates(r.data || [])).catch(() => {});
  }, []);
  const rememberTemplate = (tid) => {
    if (!tid) return;
    setRecentTplIds(prev => {
      const next = [tid, ...prev.filter(x => x !== tid)].slice(0, 3);
      try { localStorage.setItem("recent-task-templates", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const recentTemplates = recentTplIds
    .map(id => templates.find(t => t.template_id === id))
    .filter(Boolean);
  const otherTemplates = templates.filter(t => !recentTplIds.includes(t.template_id));
  const activeTpl = templates.find(t => t.template_id === tplPickTemplate);
  const activeSteps = [...(activeTpl?.tasks || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );
  const applyStep = (stepId) => {
    const step = activeSteps.find(s => s.step_id === stepId);
    if (!step) return;
    setForm(f => ({ ...f, title: step.title || f.title,
                        description: step.description || f.description }));
    rememberTemplate(tplPickTemplate);
    toast.success(`Loaded "${step.title}" from ${activeTpl.name}`);
  };
  const applyWholeTemplate = async () => {
    if (!tplPickTemplate) { toast.error("Pick a template first"); return; }
    if (!form.client_user_id) { toast.error("Choose a related client to apply to"); return; }
    setApplyingTpl(true);
    try {
      const { data } = await api.post(
        `/admin/clients/${form.client_user_id}/apply-template/${tplPickTemplate}`);
      rememberTemplate(tplPickTemplate);
      toast.success(`Applied — ${data.added || 0} step(s) added to client`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to apply");
    } finally { setApplyingTpl(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        assignee_role: "admin",
        assignee_user_id: form.assignee_user_id || null,
        client_user_id: form.client_user_id || null,
        due_date: form.due_date || null,
        due_time: form.due_time || null,
        recurrence: form.recurrence || "none",
        subtasks: subs.filter(s => s.title.trim()).map(s => ({
          title: s.title.trim(),
          due_date: s.due_date || null,
          due_time: s.due_time || null,
          notes: s.notes || "",
        })),
      };
      const { data } = await api.post("/admin/tasks", payload);
      const files = Array.from(filesRef.current?.files || []);
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        await api.post(`/admin-tasks/${data.task_id}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      // Optionally save this task as a template step
      if (saveTpl) {
        try {
          let tplId = saveTplTarget;
          if (tplId === "__new__") {
            const name = (saveTplNewName || form.title).trim();
            const { data: newTpl } = await api.post("/admin/templates",
              { name, tasks: [], kind: "onboarding" });
            tplId = newTpl.template_id;
          }
          if (tplId) {
            await api.post(`/admin/templates/${tplId}/tasks`, {
              title: form.title, description: form.description || "",
              kind: "note", order: 0,
            });
            rememberTemplate(tplId);
            toast.success("Saved as template step");
          }
        } catch (e) {
          toast.warning(formatApiErrorDetail(e.response?.data?.detail) || "Task created — template save failed");
        }
      }
      toast.success("Task created");
      setForm({ title: "", description: "", priority: "normal", due_date: "", due_time: "",
                assignee_user_id: "", client_user_id: "", recurrence: "none" });
      setSubs([{ title: "", due_date: "", due_time: "", notes: "" }]);
      setSubsOpen(false); setTplOpen(false);
      setTplPickTemplate(""); setTplPickStep("");
      setSaveTpl(false); setSaveTplTarget(""); setSaveTplNewName("");
      if (filesRef.current) filesRef.current.value = "";
      // Reload templates in case we just created one
      api.get("/admin/templates").then(r => setTemplates(r.data || [])).catch(() => {});
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const updateSub = (i, patch) =>
    setSubs(subs.map((x, j) => j === i ? { ...x, ...patch } : x));

  const filledSubs = subs.filter(s => s.title.trim()).length;

  return (
    <form onSubmit={submit} className="panel p-5 space-y-3" data-testid="new-admin-task">
      <div className="flex items-center gap-2">
        <Plus size={16} weight="bold" />
        <h3 className="title-xl text-xl">New task</h3>
      </div>
      {/* Template picker: load a task from a template, or apply the whole template to the selected client */}
      <div>
        <button type="button"
          data-testid="new-task-tpl-toggle"
          onClick={() => setTplOpen(o => !o)}
          className="w-full flex items-center justify-between gap-2 px-1 py-2 text-sm"
          style={{ borderBottom: "1px solid var(--hair)" }}>
          <span className="label" style={{ fontWeight: 700 }}>
            Use a template{tplPickTemplate ? ` · ${activeTpl?.name || ""}` : " · optional"}
          </span>
          {tplOpen ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
        </button>
        {tplOpen && (
          <div className="mt-3 space-y-2" data-testid="new-task-tpl-panel">
            <select value={tplPickTemplate} data-testid="new-task-tpl-select"
              onChange={(e) => { setTplPickTemplate(e.target.value); setTplPickStep(""); }}
              className="w-full px-3 py-2 text-sm">
              <option value="">— Choose a template —</option>
              {recentTemplates.length > 0 && (
                <optgroup label="Recent">
                  {recentTemplates.map(t => (
                    <option key={`r-${t.template_id}`} value={t.template_id}>
                      ★ {t.name}{t.kind && t.kind !== "onboarding" ? ` (${t.kind})` : ""} · {(t.tasks || []).length} task(s)
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label={recentTemplates.length > 0 ? "All templates" : "Templates"}>
                {otherTemplates.map(t => (
                  <option key={t.template_id} value={t.template_id}>
                    {t.name}{t.kind && t.kind !== "onboarding" ? ` (${t.kind})` : ""} · {(t.tasks || []).length} task(s)
                  </option>
                ))}
              </optgroup>
            </select>
            {activeSteps.length > 0 && (
              <div className="flex gap-2">
                <select value={tplPickStep} data-testid="new-task-tpl-step-select"
                  onChange={(e) => setTplPickStep(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm">
                  <option value="">— Pick a task from this template —</option>
                  {activeSteps.map(s => (
                    <option key={s.step_id} value={s.step_id}>{s.title}</option>
                  ))}
                </select>
                <button type="button" onClick={() => applyStep(tplPickStep)}
                  disabled={!tplPickStep}
                  data-testid="new-task-tpl-use-step"
                  className="btn-primary text-xs disabled:opacity-40">
                  Use this task
                </button>
              </div>
            )}
            {tplPickTemplate && (
              <button type="button" onClick={applyWholeTemplate}
                disabled={applyingTpl || !form.client_user_id}
                data-testid="new-task-tpl-apply-all"
                className="btn-ghost text-xs disabled:opacity-40">
                {applyingTpl ? "Applying…" : `Apply ALL ${activeSteps.length} tasks to selected client`}
              </button>
            )}
            {tplPickTemplate && !form.client_user_id && (
              <p className="label" style={{ color: "var(--text-dim)" }}>
                Pick a "Related client" below to enable applying the full template.
              </p>
            )}
          </div>
        )}
      </div>
      <input required placeholder="Title" value={form.title}
        data-testid="new-task-title"
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        className="w-full px-3 py-2" />
      <textarea placeholder="Notes / description" value={form.description} rows={3}
        data-testid="new-task-desc"
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        className="w-full px-3 py-2" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <p className="label mb-1">Priority</p>
          <select value={form.priority} data-testid="new-task-priority"
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
            className="w-full px-3 py-2">
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <p className="label mb-1">Due date</p>
          <input type="date" value={form.due_date} data-testid="new-task-due"
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            className="w-full px-3 py-2" />
        </div>
      </div>
      <div>
        <p className="label mb-1">Due time (optional)</p>
        <input type="time" value={form.due_time} data-testid="new-task-due-time"
          onChange={(e) => setForm({ ...form, due_time: e.target.value })}
          className="w-full px-3 py-2" />
      </div>
      <div>
        <p className="label mb-1">Recurrence</p>
        <select value={form.recurrence} data-testid="new-task-recurrence"
          onChange={(e) => setForm({ ...form, recurrence: e.target.value })}
          className="w-full px-3 py-2">
          {RECURRENCES.map(r => <option key={r.k} value={r.k}>{r.label}</option>)}
        </select>
        {form.recurrence !== "none" && (
          <p className="label mt-1" style={{ color: "var(--text-dim)" }}>
            When marked complete, a new copy will auto-generate with the next due date.
          </p>
        )}
      </div>
      <div>
        <p className="label mb-1">Assign to admin</p>
        <select value={form.assignee_user_id} data-testid="new-task-admin"
          onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })}
          className="w-full px-3 py-2">
          <option value="">Me (default)</option>
          {(admins || []).map(a => (
            <option key={a.user_id} value={a.user_id}>{`${a.name || a.email} (${a.email})`}</option>
          ))}
        </select>
      </div>
      <div>
        <p className="label mb-1">Related client (optional)</p>
        <select value={form.client_user_id} data-testid="new-task-related-client"
          onChange={(e) => setForm({ ...form, client_user_id: e.target.value })}
          className="w-full px-3 py-2">
          <option value="">None</option>
          {clients.map(c => <option key={c.user_id} value={c.user_id}>{`${c.name} (${c.email})`}</option>)}
        </select>
      </div>
      <div>
        <button type="button"
          data-testid="new-task-subs-toggle"
          onClick={() => setSubsOpen(o => !o)}
          className="w-full flex items-center justify-between gap-2 px-1 py-2 text-sm"
          style={{ borderBottom: "1px solid var(--hair)" }}>
          <span className="label" style={{ fontWeight: 700 }}>
            Subtasks{filledSubs > 0 ? ` · ${filledSubs}` : " · optional"}
          </span>
          {subsOpen ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
        </button>
        {subsOpen && (
          <div className="mt-3 space-y-3">
            {subs.map((s, i) => (
              <div key={i} className="hair p-3 space-y-2">
                <div className="flex gap-2">
                  <input value={s.title} placeholder={`Subtask ${i + 1}`}
                    data-testid={`new-task-sub-${i}`}
                    onChange={(e) => updateSub(i, { title: e.target.value })}
                    className="flex-1 px-3 py-2 text-sm" />
                  <button type="button" onClick={() => setSubs(subs.filter((_, j) => j !== i))}
                    className="btn-ghost text-xs"><Trash size={12} /></button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="label mb-1">Due date</p>
                    <input type="date" value={s.due_date}
                      data-testid={`new-task-sub-due-${i}`}
                      onChange={(e) => updateSub(i, { due_date: e.target.value })}
                      className="w-full px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <p className="label mb-1">Due time</p>
                    <input type="time" value={s.due_time}
                      data-testid={`new-task-sub-due-time-${i}`}
                      onChange={(e) => updateSub(i, { due_time: e.target.value })}
                      className="w-full px-3 py-2 text-sm" />
                  </div>
                </div>
                <div>
                  <p className="label mb-1">Notes</p>
                  <input value={s.notes} placeholder="Optional notes"
                    data-testid={`new-task-sub-notes-${i}`}
                    onChange={(e) => updateSub(i, { notes: e.target.value })}
                    className="w-full px-3 py-2 text-sm" />
                </div>
              </div>
            ))}
            <button type="button"
              onClick={() => setSubs([...subs, { title: "", due_date: "", due_time: "", notes: "" }])}
              className="btn-ghost text-xs" data-testid="new-task-add-sub">
              <Plus size={12} /> Add subtask
            </button>
          </div>
        )}
      </div>
      <div>
        <p className="label mb-1">Attachments (optional, multiple)</p>
        <FilePicker ref={filesRef} testid="new-task-files" />
      </div>
      {/* Save as template step */}
      <div className="hair p-3 space-y-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer"
          data-testid="new-task-save-tpl-label">
          <input type="checkbox" checked={saveTpl} data-testid="new-task-save-tpl"
            onChange={(e) => setSaveTpl(e.target.checked)}
            className="w-4 h-4" />
          <span className="label" style={{ fontWeight: 700 }}>
            Save this task to a template (reuse later)
          </span>
        </label>
        {saveTpl && (
          <div className="space-y-2">
            <select value={saveTplTarget}
              data-testid="new-task-save-tpl-target"
              onChange={(e) => setSaveTplTarget(e.target.value)}
              className="w-full px-3 py-2 text-sm">
              <option value="">— Where to save? —</option>
              <option value="__new__">➕ Create a new template</option>
              {templates.map(t => (
                <option key={t.template_id} value={t.template_id}>
                  Add to: {t.name}
                </option>
              ))}
            </select>
            {saveTplTarget === "__new__" && (
              <input value={saveTplNewName}
                data-testid="new-task-save-tpl-new-name"
                onChange={(e) => setSaveTplNewName(e.target.value)}
                placeholder="New template name (defaults to task title)"
                className="w-full px-3 py-2 text-sm" />
            )}
          </div>
        )}
      </div>
      <button disabled={busy} data-testid="new-task-submit"
        className="btn-primary justify-center w-full h-12">
        <PaperPlaneTilt size={14} weight="bold" /> {busy ? "Creating…" : "Create task"}
      </button>
    </form>
  );
}

function SubtaskList({ task, editable, onToggle, onDelete, onAdd, onUpdate, onChanged }) {
  const [newSub, setNewSub] = useState({ title: "", due_date: "", due_time: "", notes: "" });
  const [editing, setEditing] = useState(null); // subtask_id being edited
  const [draft, setDraft] = useState({});
  const [adderOpen, setAdderOpen] = useState(false);
  const startEdit = (s) => {
    setEditing(s.subtask_id);
    setDraft({ due_date: s.due_date || "", due_time: s.due_time || "", notes: s.notes || "" });
  };
  const saveEdit = async (s) => {
    await onUpdate(s, { due_date: draft.due_date || null, due_time: draft.due_time || null, notes: draft.notes || "" });
    setEditing(null);
  };
  const uploadSubFile = async (s, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        await api.post(`/admin-tasks/${task.task_id}/subtasks/${s.subtask_id}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Uploaded");
      onChanged && onChanged();
    } catch (_e) { toast.error("Upload failed"); }
  };
  const deleteSubFile = async (s, fileId) => {
    try {
      await api.delete(`/admin-tasks/${task.task_id}/subtasks/${s.subtask_id}/files/${fileId}`);
      onChanged && onChanged();
    } catch (_e) { toast.error("Failed"); }
  };
  return (
    <div className="mt-3">
      <p className="label mb-2">Checklist</p>
      <div className="space-y-2">
        {(task.subtasks || []).map(s => (
          <div key={s.subtask_id} className="hair p-2"
            data-testid={`subtask-${s.subtask_id}`}>
            <div className="flex items-center gap-2 text-sm">
              <button type="button" onClick={() => onToggle(s)}
                className="btn-ghost text-xs p-1" title={s.done ? "Uncheck" : "Check"}>
                {s.done ? <CheckCircle size={16} weight="fill" /> : <Circle size={16} weight="bold" />}
              </button>
              <span className="flex-1" style={{ textDecoration: s.done ? "line-through" : "none",
                             color: s.done ? "var(--text-dim)" : "inherit" }}>{s.title}</span>
              {s.due_date && editing !== s.subtask_id && (
                <span className="label px-2 py-0.5"
                  style={{ background: "var(--panel)", border: "1px solid var(--hair)", color: "var(--text-dim)" }}>
                  Due {new Date(s.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  {s.due_time ? ` · ${fmtTime(s.due_time)}` : ""}
                </span>
              )}
              {editable && (
                <>
                  <button type="button" onClick={() => editing === s.subtask_id ? setEditing(null) : startEdit(s)}
                    className="btn-ghost text-xs p-1"
                    data-testid={`subtask-edit-${s.subtask_id}`}>
                    <PencilSimple size={11} />
                  </button>
                  <label className="btn-ghost text-xs p-1 cursor-pointer"
                    data-testid={`subtask-upload-label-${s.subtask_id}`}
                    title="Attach files">
                    <UploadSimple size={11} />
                    <input type="file" multiple className="hidden"
                      data-testid={`subtask-upload-${s.subtask_id}`}
                      onChange={(e) => { uploadSubFile(s, e.target.files); e.target.value = ""; }} />
                  </label>
                  <button type="button" onClick={() => onDelete(s)}
                    className="btn-ghost text-xs p-1"><Trash size={11} /></button>
                </>
              )}
            </div>
            {s.notes && editing !== s.subtask_id && (
              <p className="text-xs mt-1 ml-8" style={{ color: "var(--text-dim)" }}>{s.notes}</p>
            )}
            {(s.files || []).length > 0 && (
              <div className="mt-2 ml-8 flex flex-wrap gap-2">
                {s.files.map(f => (
                  <span key={f.file_id} className="inline-flex items-center gap-1"
                    data-testid={`subtask-file-${f.file_id}`}>
                    <button type="button"
                      onClick={() => downloadFile(f.file_id, f.filename)}
                      className="btn-ghost text-xs">
                      <DownloadSimple size={11} weight="bold" /> {f.filename}
                    </button>
                    {editable && (
                      <button type="button"
                        onClick={() => deleteSubFile(s, f.file_id)}
                        className="btn-ghost text-xs p-1"
                        data-testid={`subtask-file-del-${f.file_id}`}>
                        <Trash size={10} />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
            {editable && editing === s.subtask_id && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2 ml-8">
                <input type="date" value={draft.due_date}
                  data-testid={`subtask-due-${s.subtask_id}`}
                  onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
                  className="px-3 py-2 text-sm" />
                <input type="time" value={draft.due_time || ""}
                  data-testid={`subtask-due-time-${s.subtask_id}`}
                  onChange={(e) => setDraft({ ...draft, due_time: e.target.value })}
                  className="px-3 py-2 text-sm" />
                <input value={draft.notes} placeholder="Notes"
                  data-testid={`subtask-notes-${s.subtask_id}`}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  className="px-3 py-2 text-sm" />
                <button type="button" onClick={() => saveEdit(s)}
                  data-testid={`subtask-save-${s.subtask_id}`}
                  className="btn-primary text-xs md:col-span-3">Save</button>
              </div>
            )}
          </div>
        ))}
      </div>
      {editable && !adderOpen && (
        <button type="button"
          data-testid={`admin-task-subtask-open-${task.task_id}`}
          onClick={() => setAdderOpen(true)}
          className="btn-ghost text-xs mt-3">
          <Plus size={12} weight="bold" /> Add subtask
        </button>
      )}
      {editable && adderOpen && (
        <div className="mt-3 hair p-3 space-y-2" data-testid={`admin-task-subtask-adder-${task.task_id}`}>
          <div className="flex gap-2">
            <input value={newSub.title} placeholder="New subtask" autoFocus
              onChange={(e) => setNewSub({ ...newSub, title: e.target.value })}
              className="flex-1 px-3 py-1.5 text-sm" />
            <button type="button"
              onClick={() => {
                if (newSub.title.trim()) {
                  onAdd(newSub);
                  setNewSub({ title: "", due_date: "", due_time: "", notes: "" });
                  setAdderOpen(false);
                }
              }}
              className="btn-primary text-xs">
              <Plus size={12} /> Add
            </button>
            <button type="button" onClick={() => { setAdderOpen(false); setNewSub({ title: "", due_date: "", due_time: "", notes: "" }); }} className="btn-ghost text-xs">✕</button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <input type="date" value={newSub.due_date}
              onChange={(e) => setNewSub({ ...newSub, due_date: e.target.value })}
              className="px-3 py-1.5 text-sm" />
            <input type="time" value={newSub.due_time}
              onChange={(e) => setNewSub({ ...newSub, due_time: e.target.value })}
              className="px-3 py-1.5 text-sm" />
            <input value={newSub.notes} placeholder="Notes (optional)"
              onChange={(e) => setNewSub({ ...newSub, notes: e.target.value })}
              className="px-3 py-1.5 text-sm" />
          </div>
        </div>
      )}
    </div>
  );
}

async function downloadFile(fileId, filename) {
  try {
    const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename || "file"; a.click();
    URL.revokeObjectURL(url);
  } catch (_e) { toast.error("Download failed"); }
}

function TaskCard({ task, clientsById, onChanged, forceOpen }) {
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const filesRef = useRef(null);
  const clientName = task.client_user_id ? clientsById[task.client_user_id]?.name : null;

  const setStatus = async (s) => {
    try {
      await api.patch(`/admin/tasks/${task.task_id}`, { status: s });
      onChanged();
    } catch (_e) { toast.error("Failed"); }
  };
  const toggleComplete = async (ev) => {
    if (ev) { ev.stopPropagation(); }
    await setStatus(task.status === "complete" ? "todo" : "complete");
  };
  const openEdit = () => {
    setDraft({
      title: task.title || "",
      description: task.description || "",
      priority: task.priority || "normal",
      due_date: task.due_date || "",
      due_time: task.due_time || "",
    });
    setEditing(true);
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/admin/tasks/${task.task_id}`, {
        title: draft.title.trim(),
        description: draft.description,
        priority: draft.priority,
        due_date: draft.due_date || null,
        due_time: draft.due_time || null,
      });
      toast.success("Task updated");
      setEditing(false);
      onChanged();
    } catch (_e) { toast.error("Failed"); }
  };
  const del = async () => {
    try { await api.delete(`/admin/tasks/${task.task_id}`); toast.success("Deleted"); onChanged(); }
    catch (_e) { toast.error("Failed"); }
  };
  const toggleSub = async (s) => {
    await api.patch(`/admin/tasks/${task.task_id}/subtasks/${s.subtask_id}`,
      { title: s.title, done: !s.done, due_date: s.due_date || null,
        due_time: s.due_time || null, notes: s.notes || "" });
    onChanged();
  };
  const updateSub = async (s, patch) => {
    await api.patch(`/admin/tasks/${task.task_id}/subtasks/${s.subtask_id}`,
      { title: s.title, done: s.done,
        due_date: patch.due_date ?? s.due_date ?? null,
        due_time: patch.due_time ?? s.due_time ?? null,
        notes: patch.notes ?? s.notes ?? "" });
    onChanged();
  };
  const delSub = async (s) => {
    await api.delete(`/admin/tasks/${task.task_id}/subtasks/${s.subtask_id}`);
    onChanged();
  };
  const addSub = async (data) => {
    await api.post(`/admin/tasks/${task.task_id}/subtasks`,
      { title: data.title.trim(), done: false,
        due_date: data.due_date || null,
        due_time: data.due_time || null,
        notes: data.notes || "" });
    onChanged();
  };
  const uploadFiles = async () => {
    const files = Array.from(filesRef.current?.files || []);
    if (!files.length) return;
    let fail = 0;
    for (const f of files) {
      try {
        const fd = new FormData(); fd.append("file", f);
        await api.post(`/admin-tasks/${task.task_id}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (_e) { fail++; }
    }
    if (filesRef.current) filesRef.current.value = "";
    if (fail) toast.error(`${fail} upload${fail === 1 ? "" : "s"} failed`);
    if (fail < files.length) toast.success("Uploaded");
    onChanged();
  };

  const setRecurrence = async (r) => {
    try {
      await api.patch(`/admin/tasks/${task.task_id}`, { recurrence: r });
      toast.success(r === "none" ? "Recurrence removed" : "Recurrence updated");
      onChanged();
    } catch (_e) { toast.error("Failed"); }
  };

  const doneSubs = (task.subtasks || []).filter(s => s.done).length;
  const totalSubs = (task.subtasks || []).length;
  const recurrence = task.recurrence || "none";
  const isComplete = task.status === "complete";
  const header = (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <input type="checkbox" checked={isComplete}
          onChange={toggleComplete}
          onClick={(e) => e.stopPropagation()}
          data-testid={`admin-task-check-${task.task_id}`}
          className="w-4 h-4 shrink-0 cursor-pointer"
          title={isComplete ? "Mark incomplete" : "Mark complete"} />
        <PriorityDot p={task.priority} />
        <span className="label px-2 py-0.5 border" style={{ borderColor: "var(--hair)", fontWeight: 700 }}>
          {task.status.replace("_", " ")}
        </span>
        <h4 className="title-xl text-lg" style={{ fontWeight: 700, textDecoration: isComplete ? "line-through" : "none", opacity: isComplete ? 0.6 : 1 }}>{task.title}</h4>
        <DueBadge due={task.due_date} dueTime={task.due_time} status={task.archived ? "complete" : task.status} />
        {recurrence !== "none" && (
          <span className="label px-2 py-0.5" style={{ background: "#e0e7ff", color: "#3730a3", border: "1px solid #c7d2fe" }}
            data-testid={`admin-task-recur-badge-${task.task_id}`}>
            ↻ {RECURRENCE_LABEL[recurrence] || recurrence}
          </span>
        )}
      </div>
      <p className="label mt-1">
        {task.assignee_role === "admin" ? "You" : `→ ${task.assignee_name}`}
        {clientName && ` · ${clientName}`}
        {task.submitted_by === "client" && " · client-submitted"}
        {totalSubs > 0 && ` · ${doneSubs}/${totalSubs} subtasks`}
      </p>
      {(task.files || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {task.files.map(f => (
            <button key={f.file_id} type="button"
              onClick={(e) => { e.stopPropagation(); downloadFile(f.file_id, f.filename); }}
              className="label px-2 py-0.5 hair"
              data-testid={`admin-task-file-chip-${f.file_id}`}
              title={`Download ${f.filename}`}
              style={{ background: "var(--panel)" }}>
              <DownloadSimple size={10} weight="bold" /> {f.filename}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <AccordionCard testid={`admin-task-${task.task_id}`} header={header} forceOpen={forceOpen}
      actions={
        confirming ? (
          <div className="flex gap-1">
            <button data-testid={`admin-task-del-yes-${task.task_id}`}
              onClick={del} className="btn-primary text-xs">Yes</button>
            <button onClick={(e) => { e.stopPropagation(); setConfirming(false); }} className="btn-ghost text-xs">✕</button>
          </div>
        ) : (
          <div className="flex gap-1">
            <button data-testid={`admin-task-edit-${task.task_id}`}
              onClick={(e) => { e.stopPropagation(); openEdit(); }}
              className="btn-ghost text-xs" title="Edit task">
              <PencilSimple size={12} weight="bold" />
            </button>
            <button data-testid={`admin-task-archive-${task.task_id}`}
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await api.patch(`/admin/tasks/${task.task_id}`, { archived: !task.archived });
                  toast.success(task.archived ? "Task restored" : "Task archived");
                  onChanged();
                } catch (_e) { toast.error("Failed"); }
              }}
              className="btn-ghost text-xs" title={task.archived ? "Restore task" : "Archive task"}>
              {task.archived ? <ArrowCounterClockwise size={12} weight="bold" /> : <Archive size={12} weight="bold" />}
            </button>
            <button data-testid={`admin-task-del-${task.task_id}`}
              onClick={(e) => { e.stopPropagation(); setConfirming(true); }} className="btn-ghost text-xs">
              <Trash size={12} weight="bold" />
            </button>
          </div>
        )
      }>
      {editing ? (
        <div className="space-y-3">
          <input value={draft.title}
            data-testid={`admin-task-edit-title-${task.task_id}`}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder="Title" className="w-full px-3 py-2 text-sm" />
          <textarea value={draft.description} rows={3}
            data-testid={`admin-task-edit-desc-${task.task_id}`}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            placeholder="Description" className="w-full px-3 py-2 text-sm" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <select value={draft.priority}
              data-testid={`admin-task-edit-priority-${task.task_id}`}
              onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
              className="px-3 py-2 text-sm">
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <input type="date" value={draft.due_date}
              data-testid={`admin-task-edit-due-${task.task_id}`}
              onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
              className="px-3 py-2 text-sm" />
            <input type="time" value={draft.due_time}
              data-testid={`admin-task-edit-due-time-${task.task_id}`}
              onChange={(e) => setDraft({ ...draft, due_time: e.target.value })}
              className="px-3 py-2 text-sm" />
          </div>
          <div className="flex gap-2">
            <button data-testid={`admin-task-edit-save-${task.task_id}`}
              onClick={saveEdit} className="btn-primary text-xs">Save</button>
            <button onClick={() => setEditing(false)} className="btn-ghost text-xs">Cancel</button>
          </div>
        </div>
      ) : (
        <>
          {task.description && (
            <p className="whitespace-pre-wrap text-sm" style={{ color: "var(--text-dim)" }}>{task.description}</p>
          )}
          <SubtaskList task={task} editable onToggle={toggleSub} onDelete={delSub}
            onAdd={addSub} onUpdate={updateSub} onChanged={onChanged} />
          <div className="mt-4 flex flex-col md:flex-row gap-2">
            <select value={task.status} data-testid={`admin-task-status-${task.task_id}`}
              onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 text-sm">
              {STATUSES.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
            <select value={recurrence} data-testid={`admin-task-recurrence-${task.task_id}`}
              onChange={(e) => setRecurrence(e.target.value)}
              className="px-3 py-2 text-sm" title="Recurrence">
              {RECURRENCES.map(r => <option key={r.k} value={r.k}>↻ {r.label}</option>)}
            </select>
            <FilePicker ref={filesRef} testid={`admin-task-files-${task.task_id}`} />
            <button onClick={uploadFiles} data-testid={`admin-task-upload-${task.task_id}`} className="btn-ghost text-xs whitespace-nowrap">
              <UploadSimple size={12} weight="bold" /> Upload
            </button>
          </div>
          <TaskNotes taskId={task.task_id}
            basePath={`/admin/tasks/${task.task_id}/notes`}
            uploadPath={`/admin-tasks/${task.task_id}/files`}
            initialNotes={task.notes || []}
            testid="admin-worktask-notes" />
        </>
      )}
    </AccordionCard>
  );
}

export default function AdminTasks() {
  const cached = getCached("admin-tasks-page");
  const [tasks, setTasks] = useState(cached?.tasks ?? []);
  const [clients, setClients] = useState(cached?.clients ?? []);
  const [admins, setAdmins] = useState(cached?.admins ?? []);
  const [filter, setFilter] = useState("open"); // open | internal | all | complete | client_submitted
  const [allOpen, setAllOpen] = useState(null);
  const [mobileTab, setMobileTab] = useState("list"); // "list" | "new"

  const load = async () => {
    try {
      const [t, c, a] = await Promise.all([
        api.get("/admin/tasks"),
        api.get("/admin/clients"),
        api.get("/admin/settings/admins"),
      ]);
      setTasks(t.data); setClients(c.data); setAdmins(a.data);
      setCached("admin-tasks-page", { tasks: t.data, clients: c.data, admins: a.data });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const clientsById = Object.fromEntries(clients.map(c => [c.user_id, c]));

  const sel = useBulkSelection();
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [clientF, setClientF] = useState("");
  const todayIso = new Date().toISOString().slice(0, 10);
  const live = tasks.filter(t => !t.archived);
  const filtered = (filter === "archived" ? tasks.filter(t => t.archived) : live.filter(t => {
    if (filter === "open") return t.status !== "complete";
    if (filter === "internal") return t.assignee_role === "admin" && t.status !== "complete" && t.submitted_by !== "client";
    if (filter === "complete") return t.status === "complete";
    if (filter === "client_submitted") return t.submitted_by === "client";
    return true;
  })).filter(t => (!onlyOverdue || (t.due_date && t.due_date.slice(0, 10) < todayIso && t.status !== "complete"))
    && (!clientF || (clientF === "none" ? !t.client_user_id : t.client_user_id === clientF)));

  const counts = {
    open: live.filter(t => t.status !== "complete").length,
    internal: live.filter(t => t.assignee_role === "admin" && t.status !== "complete" && t.submitted_by !== "client").length,
    complete: live.filter(t => t.status === "complete").length,
    client_submitted: live.filter(t => t.submitted_by === "client").length,
    archived: tasks.filter(t => t.archived).length,
  };

  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="admin-tasks-page">
      <header className="flex items-center justify-between px-4 md:px-6 py-6 border-b gap-3"
        style={{ borderColor: "var(--hair)" }}>
        <div className="flex items-center gap-3 md:gap-4 min-w-0">
          <NavButtons home="/admin" testid="tasks-back" />
          <div className="min-w-0">
            <p className="label">Console</p>
            <h1 className="title-xl text-2xl md:text-4xl mt-1 truncate">Tasks</h1>
          </div>
        </div>
      </header>
      <section className="p-4 md:p-6">
        {/* Mobile-only tabs: switch between New task and Task list */}
        <div className="lg:hidden mb-4 grid grid-cols-2 gap-2" data-testid="tasks-mobile-tabs">
          <button
            data-testid="tasks-mobile-tab-list"
            onClick={() => setMobileTab("list")}
            className={mobileTab === "list" ? "btn-primary text-sm justify-center h-10" : "btn-ghost text-sm justify-center h-10"}>
            View tasks ({tasks.length})
          </button>
          <button
            data-testid="tasks-mobile-tab-new"
            onClick={() => setMobileTab("new")}
            className={mobileTab === "new" ? "btn-primary text-sm justify-center h-10" : "btn-ghost text-sm justify-center h-10"}>
            <Plus size={12} weight="bold" /> Submit a task
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className={`lg:col-span-1 ${mobileTab === "new" ? "" : "hidden lg:block"}`}>
            <NewTaskForm clients={clients} admins={admins} onCreated={() => { load(); setMobileTab("list"); }} />
          </div>
          <div className={`lg:col-span-2 space-y-4 ${mobileTab === "list" ? "" : "hidden lg:block"}`}>
          <FilterPills
            testid="tasks-filter"
            value={filter}
            onChange={setFilter}
            options={[
              { k: "open", label: `Open (${counts.open})` },
              { k: "internal", label: `Internal (${counts.internal})` },
              { k: "client_submitted", label: `From clients (${counts.client_submitted})` },
              { k: "complete", label: `Complete (${counts.complete})` },
              { k: "archived", label: `Archived (${counts.archived})` },
              { k: "all", label: `All active (${live.length})` },
            ]}
            trailing={filtered.length > 1 ? (
              <div className="ml-auto">
                <ExpandAllToggle testid="admin-tasks-expand-all"
                  allOpen={allOpen === true} onToggle={setAllOpen} />
              </div>
            ) : null}
          />
          <div className="flex items-center gap-3 flex-wrap" data-testid="tasks-extra-filters">
            <select value={clientF} onChange={(e) => setClientF(e.target.value)} data-testid="tasks-filter-client"
              className="px-3 py-2 text-xs min-w-[180px]">
              <option value="">All clients</option>
              <option value="none">Internal (no client)</option>
              {clients.map(c => <option key={c.user_id} value={c.user_id}>{c.business_name || c.name}</option>)}
            </select>
            <label className="label flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={onlyOverdue} onChange={(e) => setOnlyOverdue(e.target.checked)}
                data-testid="tasks-filter-overdue" /> Overdue only
            </label>
          </div>
          <BulkBar testid="tasks-bulk" noun="tasks" sel={sel} visibleIds={filtered.map(t => t.task_id)}
            onApply={async (ids, archived) => { await api.post("/admin/tasks/bulk-archive", { ids, archived }); load(); }} />
          {filtered.length === 0 ? (
            <div className="panel p-10 text-center label">No tasks in this view</div>
          ) : (
            <div className="space-y-3">
              {filtered.map(t => (
                <div key={t.task_id} className="flex items-start gap-3">
                  <div className="pt-5 md:pt-6">
                    <SelectBox checked={sel.selected.has(t.task_id)} onChange={(on) => sel.toggle(t.task_id, on)}
                      testid={`tasks-select-${t.task_id}`} title="Select for bulk action" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <TaskCard task={t} clientsById={clientsById} onChanged={load}
                      forceOpen={allOpen === null ? undefined : allOpen} />
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
      </section>
    </div>
  );
}
